/**
 * Top-level row mappers — turn the `CypherReadResult` from one of the
 * Plant Viewer hierarchy queries into the fully-typed `PlantSite[]` /
 * `PlantSiteDetail` / `PlantLineDetail` / ... domain models that the
 * UI consumes. Each function is the body of the corresponding
 * endpoint's `transformResponse`, lifted out of `ontology-api.ts`.
 *
 * Drain endpoints (`getPlantSites`, `getPlantProcessTree`,
 * `getPlantAreas`) use the page-level mappers
 * (`mapPlantSiteSummaryRow`, `mapPlantProcessTreeRow`,
 * `mapPlantAreaSummaryRow`) — they operate on a single row at a time
 * since the calling `queryFn` already merged pages from
 * `drainCypherRows`. Single-row detail endpoints use the `map*` set
 * (which read `raw.results[0]`).
 */

import { extractNode, toIntCount } from '../graph-row'
import { compareByNameThenId } from '../sort'
import {
  extractComputeRefs,
  extractControllerRef,
  extractGateway,
  extractRobotControllerRefs
} from '../refs'
import {
  collectTagsFromProperties,
  extractEquipmentProperties,
  extractUdtInstances,
  mergeTagsById
} from '../extract/equipment'
import { extractPlantEdges } from '../extract/edges'
import { extractFaultList, extractProcedureList } from '../extract/equipment-relationships'
import {
  extractAreaSummaries,
  extractLineControlCoverage,
  extractLineSummaries,
  extractProcessImplementations
} from '../extract/hierarchy'
import { extractRawNode } from '../graph-row'
import type {
  CypherReadResult,
  PlantArea,
  PlantAreaDetail,
  PlantAreaSummary,
  PlantEquipment,
  PlantEquipmentDetail,
  PlantLine,
  PlantLineControlCoverage,
  PlantLineDetail,
  PlantLineInventoryItem,
  PlantPartRef,
  PlantProcess,
  PlantProcessDetail,
  PlantProcessTreeNode,
  PlantSite,
  PlantSiteDetail,
  PlantSiteSummary,
  PlantTag,
  PlantTagsBundle,
  PlantTagScope,
  PlantUtilityRef
} from '../types'

// ─── Per-row mappers (used by drained / paginated endpoints) ──────

export function mapPlantLineInventory(raw: CypherReadResult): PlantLineInventoryItem[] {
  const out: PlantLineInventoryItem[] = []
  for (const row of raw.results ?? []) {
    const id = typeof row['id'] === 'string' ? row['id'].trim() : ''
    if (!id) continue
    const rawName = typeof row['name'] === 'string' ? row['name'].trim() : ''
    out.push({
      id,
      name: rawName || id,
      equipmentCount: toIntCount(row['equipmentCount'])
    })
  }
  return out
}

export function mapPlantSiteSummaryRow(row: Record<string, unknown>): PlantSiteSummary | null {
  const site = extractNode<PlantSite>(row['s'])
  if (!site) return null
  return {
    ...site,
    lineCount: toIntCount(row['lineCount']),
    equipmentCount: toIntCount(row['equipmentCount'])
  }
}

export function mapPlantProcessTreeRow(row: Record<string, unknown>): PlantProcessTreeNode | null {
  const process = extractNode<PlantProcess>(row['p'])
  if (!process) return null
  return {
    process,
    parentId: typeof row['parentId'] === 'string' ? row['parentId'] : null,
    implementationCount: toIntCount(row['implementationCount']),
    subprocessCount: toIntCount(row['subprocessCount'])
  }
}

export function mapPlantAreaSummaryRow(row: Record<string, unknown>): PlantAreaSummary | null {
  const area = extractNode<PlantArea>(row['area'])
  if (!area) return null
  return {
    ...area,
    subAreaCount: toIntCount(row['subAreaCount']),
    equipmentCount: toIntCount(row['equipmentCount']),
    processCount: toIntCount(row['processCount']),
    partCount: toIntCount(row['partCount']),
    site: extractNode<PlantSite>(row['site']),
    parent: extractNode<PlantArea>(row['parent'])
  }
}

/**
 * Group a merged `PlantAreaSummary[]` into the (site → top-level
 * areas) buckets the Areas tab landing renders, plus a stray bucket
 * for areas not anchored to any site through the `PART_OF` chain.
 *
 * Only top-level areas (no parent area) appear on the landing grid;
 * nested sub-areas are reachable through their parent's detail page.
 * Strays surface even when nested so they don't become invisible.
 */
export function groupAreaSummaries(summaries: PlantAreaSummary[]): {
  groups: Array<{ site: PlantSite; areas: PlantAreaSummary[] }>
  strays: PlantAreaSummary[]
} {
  const groupsBySiteId = new Map<string, { site: PlantSite; areas: PlantAreaSummary[] }>()
  const strays: PlantAreaSummary[] = []
  for (const summary of summaries) {
    if (summary.parent) continue
    if (summary.site) {
      const existing = groupsBySiteId.get(summary.site.id)
      if (existing) {
        existing.areas.push(summary)
      } else {
        groupsBySiteId.set(summary.site.id, {
          site: summary.site,
          areas: [summary]
        })
      }
    } else {
      strays.push(summary)
    }
  }
  const groups = Array.from(groupsBySiteId.values()).sort((a, b) =>
    compareByNameThenId(a.site, b.site)
  )
  for (const group of groups) {
    group.areas.sort(compareByNameThenId)
  }
  strays.sort(compareByNameThenId)
  return { groups, strays }
}

// ─── Single-row detail mappers (read raw.results[0]) ──────────────

export function mapPlantSiteDetail(raw: CypherReadResult, siteId: string): PlantSiteDetail {
  const row = raw.results[0] ?? {}
  const site = extractNode<PlantSite>(row['s']) ?? {
    id: siteId,
    _id: siteId,
    name: 'Unknown site'
  }
  return {
    site,
    lines: extractLineSummaries(row['lineSummaries']),
    feeds: extractPlantEdges(row['feeds']),
    isStray: row['isStray'] === true
  }
}

export function mapPlantLineDetail(raw: CypherReadResult, lineId: string): PlantLineDetail {
  const row = raw.results[0] ?? {}
  const line = extractNode<PlantLine>(row['l']) ?? {
    id: lineId,
    _id: lineId,
    name: 'Unknown line'
  }
  return {
    line,
    isStray: row['isStray'] === true,
    parentLine: extractNode<PlantLine>(row['parentLine']),
    site: extractNode<PlantSite>(row['site']),
    process: extractNode<PlantProcess>(row['process']),
    directEquipment: extractNodeListSorted<PlantEquipment>(row['directEquipment']),
    sublines: extractLineSummaries(row['sublineSummaries']),
    sublineFeeds: extractPlantEdges(row['sublineFeeds']),
    equipmentConnects: extractPlantEdges(row['equipmentConnects']),
    controller: extractControllerRef(row['controller']),
    computes: extractComputeRefs(row['computes']),
    utilities: extractNodeListSorted<PlantUtilityRef>(row['utilities']),
    procedures: extractProcedureList(row['procedures'])
  }
}

export function mapPlantLineControlCoverage(raw: CypherReadResult): PlantLineControlCoverage {
  const row = raw.results[0] ?? {}
  return { lines: extractLineControlCoverage(row['lines']) }
}

export function mapPlantProcessDetail(
  raw: CypherReadResult,
  processId: string
): PlantProcessDetail {
  const row = raw.results[0] ?? {}
  const process = extractNode<PlantProcess>(row['p']) ?? {
    id: processId,
    _id: processId,
    name: 'Unknown process'
  }
  return {
    process,
    isStray: row['isStray'] === true,
    parent: extractNode<PlantProcess>(row['parent']),
    subprocesses: extractNodeListSorted<PlantProcess>(row['subprocesses']),
    implementations: extractProcessImplementations(row['implementations'])
  }
}

export function mapPlantEquipmentDetail(
  raw: CypherReadResult,
  equipmentId: string
): PlantEquipmentDetail {
  const row = raw.results[0] ?? {}
  const equipment = extractNode<PlantEquipment>(row['eq']) ?? {
    id: equipmentId,
    _id: equipmentId,
    name: 'Unknown equipment'
  }
  const equipmentNode = extractRawNode(row['eq'])
  if (equipmentNode) {
    equipment.properties = equipmentNode
  }
  const properties = extractEquipmentProperties(row['propBundles'])
  const udtInstances = extractUdtInstances(row['udtBundles'])
  const controllerTags = extractNodeListPlantTags(row['controllerTags'])
  const opcComputeTags = extractNodeListPlantTags(row['opcComputeTags'])
  const tags = mergeTagsById(collectTagsFromProperties(properties), controllerTags, opcComputeTags)
  return {
    equipment,
    isStray: row['isStray'] === true,
    tags,
    properties,
    udtInstances,
    utilities: extractNodeListSorted<PlantUtilityRef>(row['utilities']),
    utilityEdges: extractPlantEdges(row['utilityEdges']),
    gateway: extractGateway(row['gateway']),
    site: extractNode<PlantSite>(row['site']),
    line: extractNode<PlantLine>(row['line']),
    process: extractNode<PlantProcess>(row['process']),
    controller: extractControllerRef(row['controller']),
    robotControllers: extractRobotControllerRefs(row['robotControllers']),
    computes: extractComputeRefs(row['computes']),
    parentEquipment: extractNodeList<PlantEquipment>(row['parentEquipment'])
  }
}

export function mapPlantTagsBundle(raw: CypherReadResult, scope: PlantTagScope): PlantTagsBundle {
  const row = raw.results[0] ?? {}
  const node = extractNode<{ id: string; _id: string; name: string }>(row['n'])
  const tags = extractNodeListPlantTags(row['tags'])
  const gateway = extractGateway(row['gateway'])
  return {
    scope,
    scopeName: node?.name ?? scope.id,
    tags,
    gateway
  }
}

export function mapPlantAreaDetail(raw: CypherReadResult, areaId: string): PlantAreaDetail {
  const row = raw.results[0] ?? {}
  const area = extractNode<PlantArea>(row['a']) ?? {
    id: areaId,
    _id: areaId,
    name: 'Unknown area'
  }
  return {
    area,
    isStray: row['isStray'] === true,
    site: extractNode<PlantSite>(row['site']),
    parents: extractNodeList<PlantArea>(row['parents']),
    subAreas: extractAreaSummaries(row['subAreaSummaries']),
    equipment: extractNodeListSorted<PlantEquipment>(row['equipment']),
    processes: extractNodeListSorted<PlantProcess>(row['processes']),
    parts: extractNodeListSorted<PlantPartRef>(row['parts']),
    faults: extractFaultList(row['faults'])
  }
}

// ─── Private helpers used only by the mappers above ───────────────

function extractNodeList<T extends { id: string; _id: string; name: string }>(value: unknown): T[] {
  if (!Array.isArray(value)) return []
  return value.map((item) => extractNode<T>(item)).filter((node): node is T => node !== null)
}

function extractNodeListSorted<T extends { id: string; _id: string; name: string }>(
  value: unknown
): T[] {
  return extractNodeList<T>(value).sort(compareByNameThenId)
}

function extractNodeListPlantTags(value: unknown): PlantTag[] {
  return extractNodeList<PlantTag>(value).map((tag) => ({
    ...tag,
    label: tag.label ?? 'Tag'
  }))
}
